import { el, aplicarMascaraCpf, aplicarMascaraCnpj, cpfValido, cnpjValido, formatarCpf, formatarCnpj, limpavel } from '../lib/dom.js';
import { supabase } from '../config/supabaseClient.js';
import { renderTabela } from './table.js';
import { abrirModal } from './modal.js';
import { buscaSelect } from './buscaSelect.js';
import { toast } from '../lib/toast.js';
import { buscarTodos } from '../lib/db.js';

/**
 * CRUD genérico. cfg.semCabecalho omite o título/subtítulo (para embutir em abas).
 * cfg.enriquecer(linhas): async, anexa props calculadas após carregar (ex.: contagens).
 * cfg.acoesLinha(linha, api): retorna [botões] extras na célula de ações (antes de Editar).
 * campo.normalizar(valor): ajusta o valor antes de salvar (ex.: padronizar grafia).
 * Retorna { render(container), abrirNovo(prefill, aoSalvar), recarregar() }.
 */
export function crudPage(cfg) {
  const { titulo, singular, subtitulo, tabela, select = '*', ordenarPor = 'created_at', colunas, buscarEm, campos } = cfg;
  const nomeSingular = (singular || titulo.replace(/s$/, '')).toLowerCase();
  const limite = cfg.limite || 5000;
  const api = {};

  async function carregar() {
    try {
      const dados = await buscarTodos(
        () => {
          let q = supabase.from(tabela).select(select).order(ordenarPor, { ascending: cfg.asc !== false });
          if (cfg.filtro) q = cfg.filtro(q); // filtro base reusável (ex.: status in [ATIVO,INATIVO])
          return q;
        },
        { max: limite },
      );
      if (cfg.enriquecer) await cfg.enriquecer(dados);
      return dados;
    } catch (e) { toast('Erro ao carregar: ' + e.message, 'erro'); return []; }
  }

  async function montarForm(valores, ehEdicao) {
    const controles = {};
    const corpo = [];
    for (const campo of campos) {
      if (campo.somenteNovo && ehEdicao) continue;
      const id = `f_${campo.chave}`;
      let elemento, control;
      if (campo.tipo === 'busca') {
        let opcoes = null, label = '';
        if (campo.buscar) {
          label = (ehEdicao && campo.labelDe) ? campo.labelDe(valores) : '';
        } else {
          opcoes = typeof campo.opcoes === 'function' ? await campo.opcoes() : (campo.opcoes || []);
          const atual = valores?.[campo.chave] ?? campo.padrao ?? null;
          if (atual != null) label = opcoes.find((o) => String(o.value) === String(atual))?.label || '';
        }
        const sel = buscaSelect({ opcoes, buscar: campo.buscar || null, valor: valores?.[campo.chave] ?? campo.padrao ?? null, label, aoEscolher: campo.aoSelecionar ? (o) => campo.aoSelecionar(o, controles) : null });
        elemento = sel.elemento; control = sel;
      } else if (campo.tipo === 'select') {
        const opcoes = typeof campo.opcoes === 'function' ? await campo.opcoes() : campo.opcoes || [];
        const inp = el('select', { id }, [el('option', { value: '' }, '— selecione —'), ...opcoes.map((o) => el('option', { value: o.value }, o.label))]);
        if (valores?.[campo.chave] != null) inp.value = valores[campo.chave];
        else if (campo.padrao != null) inp.value = campo.padrao;
        elemento = inp; control = inp;
      } else if (campo.tipo === 'textarea') {
        const inp = el('textarea', { id, rows: '3' }); inp.value = valores?.[campo.chave] ?? '';
        elemento = limpavel(inp); control = inp;
      } else {
        const inp = el('input', { id, type: campo.tipo || 'text' }); inp.value = valores?.[campo.chave] ?? campo.padrao ?? '';
        if (campo.mascara === 'cpf') { inp.maxLength = 14; inp.inputMode = 'numeric'; inp.value = inp.value ? aplicarMascaraCpf(inp.value) : ''; inp.addEventListener('input', () => { inp.value = aplicarMascaraCpf(inp.value); }); }
        else if (campo.mascara === 'cnpj') { inp.maxLength = 18; inp.inputMode = 'numeric'; inp.value = inp.value ? aplicarMascaraCnpj(inp.value) : ''; inp.addEventListener('input', () => { inp.value = aplicarMascaraCnpj(inp.value); }); }
        if (campo.aoDigitar) inp.addEventListener('input', () => campo.aoDigitar(controles));
        elemento = limpavel(inp); control = inp;
      }
      controles[campo.chave] = control;
      const _filhos = [
        el('label', { for: id, text: campo.rotulo + (campo.obrigatorio || campo.obrigatorioNovo ? ' *' : '') }),
        elemento,
      ];
      if (campo.botao) _filhos.push(el('button', { type: 'button', class: 'btn', style: 'align-self:flex-start; margin-top:6px', onClick: () => campo.botao.onClick(controles) }, campo.botao.rotulo));
      if (campo.dica) _filhos.push(el('small', { class: 'campo__dica', text: campo.dica }));
      corpo.push(el('div', { class: 'campo' + ((campo.cheio || campo.tipo === 'textarea') ? ' campo--cheio' : '') }, _filhos));
    }
    const lerValores = () => {
      const v = {};
      for (const campo of campos) {
        if (!(campo.chave in controles)) continue;
        const c = controles[campo.chave];
        let valor = c.getValue ? c.getValue() : c.value;
        if (valor === '' || valor === undefined) valor = null;
        v[campo.chave] = valor;
      }
      return v;
    };
    return { corpo: [el('div', { class: 'campos-grid' }, corpo)], lerValores };
  }

  function abrirForm({ registro = null, prefill = null, aoSalvar = null }) {
    const valores = registro || prefill || null;
    const ehEdicao = !!registro;
    montarForm(valores, ehEdicao).then(({ corpo, lerValores }) => {
      abrirModal({
        titulo: (ehEdicao ? 'Editar ' : 'Novo ') + nomeSingular,
        classe: 'modal--larga',
        corpo,
        acoes: [
          { rotulo: 'Cancelar', classe: 'btn' },
          {
            rotulo: 'Salvar', classe: 'btn btn--primario',
            onClick: async () => {
              const vals = lerValores();
              const faltando = campos.find(
                (c) => ((c.obrigatorio) || (c.obrigatorioNovo && !ehEdicao)) && !(c.somenteNovo && ehEdicao) && !vals[c.chave],
              );
              if (faltando) { toast(`Preencha: ${faltando.rotulo}`, 'erro'); return false; }
              for (const c of campos) {
                if (!c.mascara || !vals[c.chave]) continue;
                if (c.mascara === 'cpf') { if (!cpfValido(vals[c.chave])) { toast(`${c.rotulo} inválido.`, 'erro'); return false; } vals[c.chave] = formatarCpf(vals[c.chave]); }
                else if (c.mascara === 'cnpj') { if (!cnpjValido(vals[c.chave])) { toast(`${c.rotulo} inválido.`, 'erro'); return false; } vals[c.chave] = formatarCnpj(vals[c.chave]); }
              }
              for (const c of campos) {
                if (c.normalizar && vals[c.chave]) vals[c.chave] = c.normalizar(vals[c.chave]);
              }
              const op = ehEdicao
                ? supabase.from(tabela).update(vals).eq('id', registro.id)
                : supabase.from(tabela).insert(vals);
              const { error } = await op;
              if (error) { toast(traduzErro(error.message), 'erro'); return false; }
              toast('Salvo com sucesso.', 'sucesso');
              api.recarregar?.();
              aoSalvar?.();
              return true;
            },
          },
        ],
      });
    });
  }

  api.abrirNovo = (prefill, aoSalvar) => abrirForm({ prefill, aoSalvar });

  api.render = async function (container) {
    container.innerHTML = '';
    if (!cfg.semCabecalho) {
      container.append(
        el('h1', { class: 'view__titulo', text: titulo }),
        el('p', { class: 'view__sub', text: subtitulo }),
      );
    }
    const tabelaBox = el('div');
    container.append(tabelaBox);
    const botaoNovo = el('button', { class: 'btn btn--primario', onClick: () => abrirForm({}) },
      [el('i', { class: 'ph ph-plus' }), ' Novo']);
    const colsComAcao = [
      ...colunas,
      { chave: '_acoes', rotulo: '', ordenavel: false,
        render: (linha) => {
          const extra = cfg.acoesLinha ? cfg.acoesLinha(linha, api) : [];
          const editar = el('button', { class: 'btn', title: 'Editar', 'aria-label': 'Editar', onClick: () => abrirForm({ registro: linha }) }, [el('i', { class: 'ph ph-pencil-simple' })]);
          return el('div', { style: 'display:flex;gap:6px;justify-content:flex-end' }, [...extra, editar]);
        } },
    ];
    let ctrl;
    async function recarregar() {
      const dados = await carregar();
      if (ctrl) ctrl.atualizar(dados);
      else ctrl = renderTabela(tabelaBox, { colunas: colsComAcao, linhas: dados, buscarEm, acoes: botaoNovo });
    }
    api.recarregar = recarregar;
    await recarregar();
  };

  return api;
}

function traduzErro(msg = '') {
  if (/duplicate key|unique/i.test(msg)) return 'Já existe um registro com esse valor único (CPF/CNPJ/número/matrícula).';
  if (/violates foreign key/i.test(msg)) return 'Referência inválida (registro relacionado não existe).';
  if (/invalid input syntax for type uuid/i.test(msg)) return 'Valor inválido: um campo que espera um código (ID) recebeu um texto comum, como um nome. Escolha a opção pela lista em vez de digitar.';
  return 'Erro ao salvar: ' + msg;
}